import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link, Outlet } from "react-router-dom";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ChevronDown, LogOut, UserCog, Library, Radar, MessageCircle, Gift, Compass, Globe, Check, Wallet, ShieldCheck, Store } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useInventory } from "@/lib/inventory";
import { useThreads } from "@/lib/chat";
import DisplayNameDialog from "@/components/DisplayNameDialog";
import RoboBallFab from "@/components/RoboBallFab";
import NextDropBanner from "@/components/NextDropBanner";
import { useShareMe } from "@/lib/share";
import { useFiguTour } from "@/components/figutour/FiguTourProvider";
import { useI18n } from "@/lib/I18nContext";
import { SUPPORTED_LANGUAGES_ORDERED, LANGUAGE_LABELS, DICTIONARIES } from "@/lib/i18n";
import PremiumShopModal from "@/components/PremiumShopModal";

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { data: inventory } = useInventory();
  const { data: threads } = useThreads({ poll: true });
  const { data: shareMe } = useShareMe();
  const { startTour } = useFiguTour();
  const { t, language, setLanguage } = useI18n();
  const [nameOpen, setNameOpen] = useState(false);
  // Phase 19b — Premium Shop modal open state.
  const [shopOpen, setShopOpen] = useState(false);
  // Phase 19c/d — mobile language sheet + mobile breakpoint detection.
  const [langSheetOpen, setLangSheetOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 767px)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  const applyLanguage = async (code) => {
    await setLanguage(code);
    const langDict = DICTIONARIES[code] || {};
    const enDict = DICTIONARIES.en;
    const msg =
      langDict["alerts.language_updated"] ||
      enDict["alerts.language_updated"];
    toast.success(msg);
  };

  // Balance from AuthContext — null while user is loading; renders skeleton.
  const balance = user ? user.figutokens_balance ?? 0 : null;

  const navItems = [
    { id: "inventory", label: t("nav.inventory"), Icon: Library, path: "/app", enabled: true, exact: true },
    { id: "radar", label: t("nav.radar"), Icon: Radar, path: "/app/radar", enabled: true, exact: true },
    { id: "market", label: t("nav.market", "Market"), Icon: Store, path: "/app/market", enabled: true, exact: false },
    { id: "chat", label: t("nav.chat"), Icon: MessageCircle, path: "/app/chat", enabled: true, exact: false },
    { id: "share", label: `${t("nav.share_earn")} 🚀`, Icon: Gift, path: "/app/share", enabled: true, exact: false },
  ];

  const displayName =
    inventory?.display_name || (user?.email ? user.email.split("@")[0] : "Collector");

  const totalUnread = (threads || []).reduce((acc, t) => acc + (t.unread_count || 0), 0);

  async function handleSignOut() {
    await logout();
    navigate("/", { replace: true });
  }

  const isActive = (item) => {
    if (!item.path) return false;
    if (item.exact) return routerLocation.pathname === item.path;
    return routerLocation.pathname === item.path || routerLocation.pathname.startsWith(item.path + "/");
  };

  return (
    <div
      data-testid="app-shell"
      className="min-h-screen w-full bg-black text-white flex flex-col"
      style={{
        // Bottom-nav offset: 80px (clears tab bar) on mobile via media query; 16px on desktop.
        "--bottom-nav-offset": "80px",
      }}
    >
      {/* ─── Top bar ─── */}
      <header
        className="sticky top-0 w-full border-b border-white/10 bg-black/85 backdrop-blur-md"
        style={{ zIndex: "var(--z-nav)" }}
      >
        <div className="flex items-center justify-between h-14 px-4 sm:px-8">
          <Link to="/app" className="figumatch-wordmark text-base sm:text-lg font-semibold" data-testid="appshell-wordmark">
            FiguMatch
          </Link>

          <div className="flex items-center gap-2">
            {/* Phase 19b — FiguWallet button (opens Premium Shop modal). */}
            <button
              type="button"
              data-testid="app-wallet-button"
              onClick={() => setShopOpen(true)}
              aria-label={t("wallet.aria_label", "Open FiguMatch Premium Shop")}
              style={{ minHeight: "44px" }}
              className="inline-flex items-center gap-2 rounded-full bg-amber-400/10 border border-amber-400/40 px-3 text-amber-300 hover:bg-amber-400/20 transition-colors"
            >
              <Wallet className="h-4 w-4" aria-hidden="true" />
              {balance == null ? (
                <span
                  data-testid="app-wallet-skeleton"
                  className="inline-block animate-pulse bg-white/10 rounded h-4 w-16"
                />
              ) : (
                <span
                  data-testid="app-wallet-balance"
                  className="text-sm font-mono"
                >
                  {balance} {t("wallet.figutokens_label")}
                </span>
              )}
            </button>

            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu-trigger"
                className="inline-flex items-center gap-2 px-3 h-9 rounded-md border border-white/10 hover:border-white/30 hover:bg-white/5 transition-colors"
              >
                <span className="font-body text-sm text-white max-w-[140px] sm:max-w-[200px] truncate">
                  {displayName}
                </span>
                <ChevronDown className="h-3.5 w-3.5 text-[#A1A1AA]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-[#0A0A0A] border-white/10 text-white"
            >
              <DropdownMenuLabel className="text-[#A1A1AA] font-body text-xs">
                {user?.email}
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem
                onClick={() => setNameOpen(true)}
                className="cursor-pointer hover:bg-white/5 focus:bg-white/5 text-white"
                data-testid="menu-edit-display-name"
              >
                <UserCog className="h-4 w-4 mr-2 text-[#E5B83A]" />
                Edit display name
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => startTour()}
                className="cursor-pointer hover:bg-white/5 focus:bg-white/5 text-white"
                data-testid="menu-take-figutour"
              >
                <Compass className="h-4 w-4 mr-2 text-[#E5B83A]" />
                Take the FiguTour
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/10" />
              {/* Phase 18 — nested language sub-menu (right-fly-out on
                  desktop). Phase 19c/d — on mobile (<768 px) the sub-menu
                  is replaced with a bottom Sheet triggered from the same
                  dropdown item to avoid off-viewport clipping. */}
              {isMobile ? (
                <DropdownMenuItem
                  data-testid="menu-language-trigger"
                  onSelect={(e) => {
                    e.preventDefault();
                    setLangSheetOpen(true);
                  }}
                  className="cursor-pointer hover:bg-white/5 focus:bg-white/5 text-white"
                >
                  <Globe className="h-4 w-4 mr-2 text-[#E5B83A]" />
                  <span>{t("app.language_menu_label", "Language")}</span>
                </DropdownMenuItem>
              ) : (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger
                    data-testid="menu-language-trigger"
                    className="cursor-pointer hover:bg-white/5 focus:bg-white/5 text-white"
                  >
                    <Globe className="h-4 w-4 mr-2 text-[#E5B83A]" />
                    <span>{t("app.language_menu_label", "Language")}</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent
                      data-testid="menu-language-sub"
                      className="bg-[#0A0A0A] border-white/10 text-white min-w-[220px]"
                    >
                      {SUPPORTED_LANGUAGES_ORDERED.map((code) => {
                        const active = language === code;
                        return (
                          <DropdownMenuItem
                            key={code}
                            data-testid={`app-language-item-${code}`}
                            data-active={active ? "true" : "false"}
                            onSelect={() => applyLanguage(code)}
                            className="cursor-pointer hover:bg-white/5 focus:bg-white/5 text-white flex items-center"
                          >
                            <span className="flex-1">{LANGUAGE_LABELS[code]}</span>
                            {active ? (
                              <Check
                                className="h-4 w-4 ml-2 text-[#E5B83A]"
                                data-testid={`app-language-check-${code}`}
                              />
                            ) : null}
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>
              )}
              {/* Phase 19c/d — Admin Dashboard entry, gated by is_admin. */}
              {user?.is_admin ? (
                <DropdownMenuItem
                  data-testid="menu-admin-dashboard"
                  onClick={() => navigate("/app/admin")}
                  className="cursor-pointer hover:bg-white/5 focus:bg-white/5 text-white"
                >
                  <ShieldCheck className="h-4 w-4 mr-2 text-[#E5B83A]" />
                  {t("admin.nav_item", "Admin Dashboard")}
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem
                onClick={handleSignOut}
                className="cursor-pointer hover:bg-white/5 focus:bg-white/5 text-white"
                data-testid="menu-sign-out"
              >
                <LogOut className="h-4 w-4 mr-2 text-[#A1A1AA]" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* ─── Desktop sidebar ─── */}
        <aside
          data-testid="sidebar"
          className="hidden md:flex flex-col w-56 shrink-0 border-r border-white/10 bg-black px-4 py-6 gap-1 overflow-y-auto"
        >
          {navItems.map(({ id, label, Icon, enabled, path }) => (
            <NavItem
              key={id}
              label={label}
              Icon={Icon}
              enabled={enabled}
              active={isActive({ path, exact: id !== "chat" })}
              path={path}
              testid={`nav-${id}`}
              domId={
                id === "radar"
                  ? "radar-tab"
                  : id === "share"
                  ? "share-earn-item"
                  : undefined
              }
              dot={id === "chat" && totalUnread > 0}
            />
          ))}
          <NextDropBanner />

          {/* Legal footer — pinned to sidebar bottom via mt-auto so it
              never shifts any element above (nav items, NextDropBanner). */}
          <SidebarLegalFooter refCode={shareMe?.referral_code} />
        </aside>

        {/* ─── Main content ─── */}
        <main className="flex-1 min-w-0 px-4 sm:px-8 py-6 sm:py-10 pb-24 md:pb-10">
          {/* Phase 12 fix — the sidebar `<aside>` above is `hidden md:flex`,
              which correctly hides the desktop nav on mobile but also hid
              the "Beyond Football…" teaser (NextDropBanner) below <768 px.
              Re-render a mobile-only copy here so the expansion teaser
              stays visible on all viewports. Wrapped in `md:hidden` so it
              doesn't double up next to the sidebar copy on desktop. */}
          <div className="md:hidden mb-6" data-testid="nextdrop-banner-mobile">
            <NextDropBanner />
          </div>
          <Outlet />
        </main>
      </div>

      {/* ─── Mobile bottom tab bar ─── */}
      <nav
        data-testid="bottom-tabs"
        className="md:hidden fixed bottom-0 left-0 right-0 border-t border-white/10 bg-black/95 backdrop-blur-md"
        style={{ zIndex: "var(--z-nav)" }}
      >
        <ul className="flex items-stretch justify-around h-16">
          {navItems.map(({ id, label, Icon, enabled, path }) => {
            const active = isActive({ path, exact: id !== "chat" });
            const showDot = id === "chat" && totalUnread > 0;
            const className =
              "w-full h-full flex flex-col items-center justify-center gap-0.5 text-xs font-body transition-colors relative " +
              (enabled
                ? active
                  ? "text-[#E5B83A]"
                  : "text-white/70 hover:text-white"
                : "text-[#555] cursor-not-allowed");
            const inner = (
              <>
                <span className="relative">
                  <Icon className="h-4 w-4" />
                  {showDot ? (
                    <span
                      data-testid="chat-unread-dot-mobile"
                      className="absolute -top-0.5 -right-1.5 h-2 w-2 rounded-full bg-[#E5B83A] ring-2 ring-black"
                    />
                  ) : null}
                </span>
                <span>{label}</span>
                {!enabled ? (
                  <span className="text-[8px] tracking-widest text-[#444]">SOON</span>
                ) : null}
              </>
            );
            return (
              <li key={id} className="flex-1">
                {enabled && path ? (
                  <Link to={path} data-testid={`bottom-nav-${id}`} className={className}>
                    <span className="relative">
                      <Icon
                        className="h-4 w-4"
                        data-testid={id === "radar" ? "radar-tab-icon" : undefined}
                      />
                      {showDot ? (
                        <span
                          data-testid="chat-unread-dot-mobile"
                          className="absolute -top-0.5 -right-1.5 h-2 w-2 rounded-full bg-[#E5B83A] ring-2 ring-black"
                        />
                      ) : null}
                    </span>
                    <span>{label}</span>
                    {!enabled ? (
                      <span className="text-[8px] tracking-widest text-[#444]">SOON</span>
                    ) : null}
                  </Link>
                ) : (
                  <button type="button" disabled data-testid={`bottom-nav-${id}`} className={className}>
                    {inner}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      <DisplayNameDialog
        open={nameOpen}
        onOpenChange={setNameOpen}
        currentName={displayName}
      />

      <PremiumShopModal open={shopOpen} onOpenChange={setShopOpen} />

      {/* Phase 19c/d — Mobile-only language Sheet (rendered outside
          DropdownMenu so it can host a Radix Dialog without collision). */}
      <Sheet open={langSheetOpen} onOpenChange={setLangSheetOpen}>
        <SheetContent
          data-testid="app-language-sheet"
          side="bottom"
          className="bg-[#0A0A0A] border-t border-white/10 text-white"
        >
          <SheetHeader className="text-left">
            <SheetTitle className="text-white">
              {t("app.language_menu_label", "Language")}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-4 flex flex-col divide-y divide-white/10">
            {SUPPORTED_LANGUAGES_ORDERED.map((code) => {
              const active = language === code;
              return (
                <button
                  key={code}
                  type="button"
                  data-testid={`app-language-item-${code}`}
                  data-active={active ? "true" : "false"}
                  onClick={async () => {
                    await applyLanguage(code);
                    setLangSheetOpen(false);
                  }}
                  className="w-full flex items-center justify-between py-3 text-left text-white hover:bg-white/5 focus:bg-white/5 min-h-[48px]"
                >
                  <span className="text-sm font-body">
                    {LANGUAGE_LABELS[code]}
                  </span>
                  {active ? (
                    <Check
                      className="h-4 w-4 text-[#E5B83A]"
                      data-testid={`app-language-check-${code}`}
                    />
                  ) : null}
                </button>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>

      <RoboBallFab />
    </div>
  );
}

function NavItem({ label, Icon, enabled, active, path, testid, dot, domId }) {
  const className =
    "w-full text-left px-3 py-2.5 rounded-md flex items-center gap-3 font-body text-sm transition-colors relative " +
    (active
      ? "bg-[#E5B83A]/10 text-[#E5B83A] border border-[#E5B83A]/30"
      : enabled
      ? "text-white hover:bg-white/5 border border-transparent"
      : "text-[#555] cursor-not-allowed border border-transparent");
  const inner = (
    <>
      <span className="relative">
        <Icon className="h-4 w-4 shrink-0" />
        {dot ? (
          <span
            data-testid="chat-unread-dot"
            className="absolute -top-0.5 -right-1 h-2 w-2 rounded-full bg-[#E5B83A] ring-2 ring-black"
          />
        ) : null}
      </span>
      <span className="flex-1">{label}</span>
      {!enabled ? (
        <span className="text-[10px] tracking-widest text-[#444]">SOON</span>
      ) : null}
    </>
  );
  if (enabled && path) {
    return (
      <Link id={domId} to={path} data-testid={testid} className={className}>
        {inner}
      </Link>
    );
  }
  return (
    <button id={domId} type="button" disabled={!enabled} data-testid={testid} className={className}>
      {inner}
    </button>
  );
}

function SidebarLegalFooter({ refCode }) {
  const api = process.env.REACT_APP_BACKEND_URL;
  const q = refCode ? `&ref=${encodeURIComponent(refCode)}` : "";
  return (
    <div
      data-testid="sidebar-legal-footer"
      className="mt-auto pt-4 pb-2 flex flex-col gap-1 text-[10px] text-neutral-600 font-body"
    >
      <a
        data-testid="legal-link-terms-sidebar"
        href={`${api}/api/legal/track-open?document_type=terms${q}`}
        className="hover:text-amber-400 transition-colors"
      >
        Terms of Service
      </a>
      <a
        data-testid="legal-link-privacy-sidebar"
        href={`${api}/api/legal/track-open?document_type=privacy${q}`}
        className="hover:text-amber-400 transition-colors"
      >
        Privacy Policy
      </a>
    </div>
  );
}
